import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const SEOHead = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  structuredData,
  noindex = false,
  canonical
}) => {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';
  
  // Default values
  const defaultTitle = isArabic 
    ? 'بديل - منصة تبادل وبيع معدات المختبرات الطبية في السعودية'
    : 'Badeel - Medical Laboratory Equipment Exchange Platform in Saudi Arabia';
    
  const defaultDescription = isArabic
    ? 'منصة بديل الرائدة في المملكة العربية السعودية لتبادل وبيع معدات المختبرات الطبية. اكتشف أجهزة تحليل الدم، معدات التشخيص، والأدوات الطبية بأفضل الأسعار.'
    : 'Badeel is the leading platform in Saudi Arabia for exchanging and selling medical laboratory equipment. Discover blood analysis devices, diagnostic equipment, and medical tools at the best prices.';
    
  const defaultKeywords = isArabic
    ? 'بديل، معدات مختبرات، أجهزة طبية، تحليل الدم، معدات تشخيص، مختبرات السعودية، أدوات طبية، تبادل معدات، بيع معدات طبية'
    : 'badeel, laboratory equipment, medical devices, blood analysis, diagnostic equipment, saudi labs, medical tools, equipment exchange, medical equipment sales';

  const finalTitle = title ? `${title} | بديل` : defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalKeywords = keywords || defaultKeywords;
  const finalImage = image || `${window.location.origin}/logo512.png`;
  const finalUrl = url || window.location.href;
  const finalCanonical = canonical || window.location.href;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      
      {/* Language and Direction */}
      <html lang={isArabic ? 'ar' : 'en'} dir={isArabic ? 'rtl' : 'ltr'} />
      
      {/* Robots */}
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={finalCanonical} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:site_name" content="بديل - منصة المختبرات الطبية" />
      <meta property="og:locale" content={isArabic ? 'ar_SA' : 'en_US'} />
      {isArabic && <meta property="og:locale:alternate" content="en_US" />}
      {!isArabic && <meta property="og:locale:alternate" content="ar_SA" />}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={finalUrl} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
      <meta name="twitter:site" content="@badeel_platform" />
      <meta name="twitter:creator" content="@badeel_platform" />
      
      {/* Additional Meta Tags */}
      <meta name="author" content="بديل - منصة المختبرات الطبية" />
      <meta name="publisher" content="بديل" />
      <meta name="application-name" content="بديل" />
      <meta name="theme-color" content="#1e40af" />
      
      {/* Geo Tags for Saudi Arabia */}
      <meta name="geo.region" content="SA" />
      <meta name="geo.country" content="Saudi Arabia" />
      <meta name="geo.placename" content="Riyadh" />
      <meta name="ICBM" content="24.7136, 46.6753" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Predefined SEO configurations for common pages
export const HomeSEO = () => (
  <SEOHead
    title={null} // Will use default
    structuredData={{
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "بديل - منصة المختبرات الطبية",
      "url": "https://badeil.com/",
      "logo": "https://badeil.com/logo512.png",
      "description": "منصة بديل الرائدة في المملكة العربية السعودية لتبادل وبيع معدات المختبرات الطبية",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "SA",
        "addressLocality": "الرياض"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "availableLanguage": ["Arabic", "English"]
      }
    }}
  />
);

export const ProductSEO = ({ product }) => {
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "image": product.images?.map(img => img.url) || [],
    "brand": {
      "@type": "Brand",
      "name": product.brand || "غير محدد"
    },
    "category": product.category?.name?.ar || product.category,
    "sku": product.sku || product._id,
    "gtin": product.gtin || product.barcode,
    "mpn": product.model || product.mpn,
    "condition": "https://schema.org/" + (product.condition === 'new' ? 'NewCondition' : 'UsedCondition'),
    "offers": product.type === 'sale' ? {
      "@type": "Offer",
      "price": product.price,
      "priceCurrency": "SAR",
      "availability": product.quantity > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "url": `https://badeil.com/products/${product.slug || product._id}`,
      "seller": {
        "@type": "Organization",
        "name": "بديل - منصة المختبرات الطبية",
        "url": "https://badeil.com"
      },
      "validFrom": product.createdAt,
      "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // سنة من الآن
      "itemCondition": "https://schema.org/" + (product.condition === 'new' ? 'NewCondition' : 'UsedCondition'),
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0",
          "currency": "SAR"
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "businessDays": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Sunday"]
          },
          "cutoffTime": "14:00"
        }
      }
    } : {
      "@type": "Offer",
      "price": product.actualValue?.amount || 0,
      "priceCurrency": "SAR",
      "availability": product.quantity > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "url": `https://badeil.com/products/${product.slug || product._id}`,
      "seller": {
        "@type": "Organization", 
        "name": "بديل - منصة المختبرات الطبية",
        "url": "https://badeil.com"
      },
      "itemCondition": "https://schema.org/" + (product.condition === 'new' ? 'NewCondition' : 'UsedCondition')
    },
    "aggregateRating": product.averageRating ? {
      "@type": "AggregateRating",
      "ratingValue": product.averageRating,
      "reviewCount": product.reviewCount || 1,
      "bestRating": 5,
      "worstRating": 1
    } : undefined,
    "manufacturer": {
      "@type": "Organization",
      "name": product.manufacturer || product.brand || "غير محدد"
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "الحالة",
        "value": product.condition === 'new' ? 'جديد' : 'مستعمل'
      },
      {
        "@type": "PropertyValue", 
        "name": "النوع",
        "value": product.type === 'sale' ? 'للبيع' : 'للتبادل'
      },
      {
        "@type": "PropertyValue",
        "name": "الكمية المتاحة",
        "value": product.quantity
      }
    ].filter(Boolean)
  };

  return (
    <SEOHead
      title={`${product.name} - أجهزة مختبرات طبية للبيع`}
      description={`${product.description} - اشترِ ${product.name} بأفضل سعر في السعودية. ${product.type === 'sale' ? `السعر: ${product.price} ريال` : 'متاح للتبادل'} مع ضمان الجودة وشحن مجاني.`}
      keywords={`${product.name}, ${product.category?.name?.ar || product.category}, معدات مختبرات, أجهزة طبية, بديل, ${product.brand || ''}, مستلزمات مختبر`}
      image={product.images?.[0]?.url}
      type="product"
      structuredData={productSchema}
    />
  );
};

export const CategorySEO = ({ category, productsCount }) => (
  <SEOHead
    title={`${category.name?.ar || category.name} - معدات مختبرات طبية`}
    description={`اكتشف ${productsCount} منتج في فئة ${category.name?.ar || category.name} من المعدات الطبية عالية الجودة في منصة بديل`}
    keywords={`${category.name?.ar || category.name}, معدات مختبرات, أجهزة طبية, بديل`}
    structuredData={{
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": `${category.name?.ar || category.name} - بديل`,
      "description": `مجموعة من ${category.name?.ar || category.name} في منصة بديل`,
      "url": window.location.href,
      "mainEntity": {
        "@type": "ItemList",
        "numberOfItems": productsCount
      }
    }}
  />
);

export const LabSEO = ({ lab }) => (
  <SEOHead
    title={`${lab.labName} - مختبر طبي معتمد`}
    description={`تعرف على ${lab.labName} - مختبر طبي معتمد في منصة بديل. اكتشف المنتجات والخدمات المتاحة`}
    keywords={`${lab.labName}, مختبر طبي, معدات طبية, بديل`}
    type="profile"
    structuredData={{
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": lab.labName,
      "description": `مختبر طبي معتمد في منصة بديل`,
      "url": window.location.href,
      "memberOf": {
        "@type": "Organization",
        "name": "بديل - منصة المختبرات الطبية"
      }
    }}
  />
);

export const SearchSEO = ({ query, resultsCount }) => (
  <SEOHead
    title={`البحث عن "${query}" - نتائج المعدات الطبية`}
    description={`نتائج البحث عن "${query}" في منصة بديل. ${resultsCount} منتج متاح من المعدات الطبية والمختبرات`}
    keywords={`${query}, بحث, معدات مختبرات, أجهزة طبية, بديل`}
    noindex={true} // لا نريد فهرسة صفحات البحث
  />
);

export default SEOHead;